import globals from "@lvt/globals";
import { createApi, BaseQueryFn } from "@reduxjs/toolkit/query/react";
import Ajv from "ajv";
import axios, { AxiosRequestConfig } from "axios";
import applyCaseMiddleware from "axios-case-converter";

import { LvtResponse2 } from "./common";


export interface VmsApiExtraOptions {
    validator?: Ajv.ValidateFunction;
}

const axiosClient = applyCaseMiddleware(
    axios.create({
        baseURL: globals.userdashboard.proxyUrl,
        withCredentials: true,
    }),
);

type VmsApiError = ValidationError | ResponseError;

interface ValidationError {
    type: "VALIDATION_ERROR";
    data?: string;
    error: string;
}

interface ResponseError {
    type: "RESPONSE_ERROR";
    error: any;
    status: number;
}

interface VmsApiArg {
    url: string;
    method: AxiosRequestConfig["method"];
    body?: AxiosRequestConfig["data"];
}

// eslint-disable-next-line @typescript-eslint/ban-types
type VmsApiMeta = {
};

type VmsApiResponse = LvtResponse2<unknown>;

/*
    Base query.

    Performs validation if you pass in a validator in the extraOptions.

    Currently handles LvtResponse2 structure.
*/
export const vmsBaseQuery = (baseUrl: string): BaseQueryFn<VmsApiArg, VmsApiResponse, VmsApiError, VmsApiExtraOptions, VmsApiMeta> => async (
    { url, method, body },
    api,
    extraOptions,
) => {
    try {
        const result = await axiosClient({
            url: baseUrl + url,
            method,
            data: body,
        });

        const validator = extraOptions?.validator;

        if (validator != null) {
            if (!validator(result.data)) {
                const validationError: VmsApiError = {
                    type: "VALIDATION_ERROR",
                    error: JSON.stringify(validator.errors),
                    data: JSON.stringify(result.data),
                };

                console.warn("validation failed", validationError);

                return { error: validationError };
            }
        }

        return { data: result.data.data };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            if (error.response && error.response.data.error) {
                const { data: responseData, status } = error.response;
                const responseError: ResponseError = {
                    type: "RESPONSE_ERROR",
                    status: status,
                    error: responseData.error,
                };

                return { error: responseError };
            }
        }

        throw error;
    }
};

/*
    RTKQuery api.

    You can use the injectApi function to inject endpoints into the api.

    See /api/liveUnits/deafultCamera.ts for an example.
*/
export const vmsBaseApi = createApi({
    reducerPath: "vms-base",
    baseQuery: vmsBaseQuery(globals.userdashboard.proxyUrl),
    endpoints: () => ({}),
});
